import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { oktaAuth } from "@/App";
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: unknown;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

let api: AxiosInstance | null = null;

function safeGetProfile() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem("userProfile");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function getAccessTokenSafe(): Promise<string | undefined> {
  try {
    const tokenObj = await oktaAuth.tokenManager.get("accessToken");
    if (tokenObj && typeof tokenObj === "object" && "accessToken" in tokenObj) {
      return (tokenObj as any).accessToken;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function buildApi(): AxiosInstance {
  const instance = axios.create({
    baseURL: BASE_URL
  });

  instance.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {};
    // Prefer fresh token from Okta
    const token = await getAccessTokenSafe();
    if (token) {
      config.headers["AccessToken"] = token;
    }
    config.headers["SubCompanyCode"] = "CDP";
    return config;
  });

  return instance;
}

function getApi(): AxiosInstance {
  if (!api) api = buildApi();
  return api;
}

export function createApiInstance(accessToken?: string) {
  api = axios.create({
    baseURL: BASE_URL,
    headers: {
      AccessToken: accessToken ?? ""
    }
  });
  return api;
}

export async function apiGet<T = unknown>(
  url: string,
  params: object = {},
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response: AxiosResponse<T> = await getApi().get(url, { params, ...config });
  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

export async function apiPost<T = unknown>(
  url: string,
  body: any = {},
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const isFormData = body instanceof FormData;

  const response: AxiosResponse<T> = await getApi().post(url, body, {
    ...config,
    headers: {
        Accept:"*/*",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...config?.headers
    }
  });

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

export async function apiPut<T = unknown>(
  url: string,
  body: any = {},
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response: AxiosResponse<T> = await getApi().put(url, body, {
    ...config,
    headers: {
      "Content-Type": "application/json",
      ...config?.headers
    }
  });

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

export async function apiPatch<T = any>(
  url: string,
  body: any = {},
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response: AxiosResponse<T> = await getApi().patch(url, body, {
    ...config,
    headers: {
      "Content-Type": "application/json",
      ...config?.headers
    }
  });

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

export async function apiDelete<T = any>(
  url: string,
  body: object = {},
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response: AxiosResponse<T> = await getApi().delete(url, {
    data: body,
    ...config
  });

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
  createApiInstance
};